/**
 * Day Column
 * Individual day column in the kanban board with routine selection and exercises
 */

import {
  Copy,
  FolderInput,
  MoreVertical,
  Plus,
  Pointer,
  Trash2
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { DAY_DISPLAY_NAMES, type DayOfWeek } from '../../lib/constants'
import { cn } from '../../lib/utils'
import { useSplitConfigStore } from '../../stores/splitConfigStore'
import { useSplitUIStore } from '../../stores/splitUIStore'
import { UnifiedButton } from '../ui/unified-button'
import { SortableExerciseCard } from './SortableExerciseCard'

interface DayColumnProps {
  day: DayOfWeek
}

export const DayColumn = ({ day }: DayColumnProps) => {
  const {
    getRoutineForDay,
    createRoutine,
    assignRoutineToDay,
    unassignRoutineFromDay,
    updateRoutineName,
    reorderExercisesInRoutine
  } = useSplitConfigStore()

  const {
    copiedRoutineId,
    openExerciseModal,
    openRoutineModal,
    copyRoutineToClipboard,
    clearClipboard
  } = useSplitUIStore()

  const assignedRoutine = getRoutineForDay(day)
  const [localRoutineName, setLocalRoutineName] = useState('')
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  
  // DnD sensors for better UX
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false)
      }
    }

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isMenuOpen])

  const handleCreateAndAssignRoutine = () => {
    const routineId = createRoutine('')
    assignRoutineToDay(day, routineId)
  }

  const handleCopyRoutine = () => {
    if (assignedRoutine) {
      copyRoutineToClipboard(assignedRoutine.id)
    }
    setIsMenuOpen(false)
  }

  const handleRemoveRoutine = () => {
    unassignRoutineFromDay(day)
    setIsMenuOpen(false)
  }


  const handleUseClipboardRoutine = () => {
    if (copiedRoutineId) {
      assignRoutineToDay(day, copiedRoutineId)
      clearClipboard()
    }
  }

  const handleAddExercise = () => {
    if (assignedRoutine) {
      openExerciseModal(day, assignedRoutine.id)
    }
  }

  const handleRoutineNameChange = (newName: string) => {
    setLocalRoutineName(newName)
    if (assignedRoutine) {
      updateRoutineName(assignedRoutine.id, newName)
    }
  }
  
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    
    if (over && active.id !== over.id && assignedRoutine) {
      const oldIndex = assignedRoutine.exercises.findIndex(ex => ex.id === active.id)
      const newIndex = assignedRoutine.exercises.findIndex(ex => ex.id === over.id)
      
      if (oldIndex !== -1 && newIndex !== -1) {
        reorderExercisesInRoutine(assignedRoutine.id, oldIndex, newIndex)
      }
    }
  }

  // Show empty slot if no routine assigned
  if (!assignedRoutine) {
    if (copiedRoutineId) {
      // Show "Use here" state when there's a routine in clipboard
      return (
        <div className="flex min-w-76 flex-col">
          {/* Day name above the card */}
          <h3 className="text-muted-foreground mb-2 text-center text-sm font-medium">
            {DAY_DISPLAY_NAMES[day]}
          </h3>

          {/* Use here card - entire card is clickable */}
          <div
            onClick={handleUseClipboardRoutine}
            className="border-primary/50 bg-primary/5 hover:border-primary hover:bg-primary/10 flex min-h-96 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed p-4"
          >
            <div className="text-primary flex items-center gap-2">
              <span className="text-sm font-medium">Use here</span>
              <Pointer className="h-4 w-4" />
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className="flex min-w-76 flex-col">
        {/* Day name above the card */}
        <h3 className="text-muted-foreground mb-2 text-center text-sm font-medium">
          {DAY_DISPLAY_NAMES[day]}
        </h3>

        {/* Empty slot wrapper - matches kanban card min height */}
        <div className="flex min-h-96 flex-col gap-1 rounded-lg">
          {/* Add new routine card */}
          <div
            onClick={handleCreateAndAssignRoutine}
            className="border-muted-foreground/30 hover:border-muted-foreground/50 hover:bg-muted/20 flex flex-1 cursor-pointer items-center justify-center rounded-t-lg border-2 border-dashed bg-transparent p-4"
          >
            <div className="text-muted-foreground flex flex-col items-center gap-2">
              <Plus className="h-6 w-6" />
              <span className="text-sm">Add Routine</span>
            </div>
          </div>

          {/* Use existing routine card */}
          <div
            onClick={() => openRoutineModal(day)}
            className="border-muted-foreground/30 hover:border-muted-foreground/50 hover:bg-muted/20 flex flex-1 cursor-pointer items-center justify-center rounded-b-lg border-2 border-dashed bg-transparent p-4"
          >
            <div className="text-muted-foreground flex flex-col items-center gap-2">
              <FolderInput className="h-6 w-6" />
              <span className="text-sm">Use existing routine</span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Show routine card when routine is assigned
  return (
    <div className="flex min-w-76 flex-col">
      {/* Day name above the card */}
      <h3 className="text-muted-foreground mb-2 text-center text-sm font-medium">
        {DAY_DISPLAY_NAMES[day]}
      </h3>

      {/* Routine card */}
      <div className="bg-card relative min-h-96 rounded-lg border p-3">
        {/* Overflow menu - positioned at top right */}
        <div ref={menuRef} className="absolute top-2 right-2">
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="text-muted-foreground hover:bg-muted hover:text-foreground focus:ring-ring flex h-7 w-7 cursor-pointer items-center justify-center rounded transition-colors focus:ring-2 focus:ring-offset-2 focus:outline-none"
            title="Routine options"
          >
            <MoreVertical className="h-4 w-4" />
          </button>

          {/* Dropdown menu */}
          {isMenuOpen && (
            <div
              className={cn(
                'bg-popover text-popover-foreground absolute right-0 mt-1 w-40 overflow-hidden rounded-md border p-1 shadow-md',
                'animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-200',
                'z-50'
              )}
            >
              <button
                onClick={handleCopyRoutine}
                className="hover:bg-accent hover:text-accent-foreground flex w-full cursor-pointer items-center rounded-sm px-2 py-1.5 text-sm transition-colors outline-none"
              >
                <Copy className="mr-2 h-4 w-4" />
                <span>Copy</span>
              </button>
              <button
                onClick={handleRemoveRoutine}
                className="hover:bg-accent hover:text-accent-foreground flex w-full cursor-pointer items-center rounded-sm px-2 py-1.5 text-sm transition-colors outline-none"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                <span>Remove</span>
              </button>
            </div>
          )}
        </div>

        {/* Routine name input */}
        <div className="mb-3 pr-8">
          <input
            type="text"
            value={localRoutineName || assignedRoutine.name}
            onChange={(e) => handleRoutineNameChange(e.target.value)}
            className="placeholder:text-muted-foreground w-full bg-transparent text-base font-medium outline-none"
            placeholder={`${DAY_DISPLAY_NAMES[day]} Workout`}
          />
          <p className="text-muted-foreground mt-1 text-xs">
            {assignedRoutine.exercises.length} exercise
            {assignedRoutine.exercises.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Exercise list */}
        <div className="min-h-64">
          {/* Exercise list items with drag and drop */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={assignedRoutine.exercises.map(ex => ex.id)}
              strategy={verticalListSortingStrategy}
            >
              {assignedRoutine.exercises.map((exercise, _index) => (
                <div key={exercise.id} className={cn('border-border/50 border-b')}>
                  <SortableExerciseCard exercise={exercise} routineId={assignedRoutine.id} />
                </div>
              ))}
            </SortableContext>
          </DndContext>

          {/* Add exercise button at bottom */}
          <UnifiedButton
            variant="ghost"
            size="sm"
            onClick={handleAddExercise}
            className="border-border/50 text-muted-foreground hover:text-foreground mt-4 w-full border border-dashed"
          >
            <div className="mx-auto flex items-center gap-2">
              <Plus className="mr-2 h-4 w-4" />
              Add Exercise
            </div>
          </UnifiedButton>
        </div>
      </div>
    </div>
  )
}
